import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { LogOut, User as UserIcon } from 'lucide-react'
import { Badge } from '@/components/ui'
import { useAuthStore } from '@/store/auth.store'
import { useRoleStore } from '@/store/role.store'
import { authService } from '@/services/auth.service'
import type { UserRole } from '@/types/user.types'

const ROLE_BADGE_VARIANT: Record<UserRole, 'gold' | 'teal' | 'ember' | 'orange'> = {
  attendee:  'gold',
  organiser: 'gold',
  vendor:    'teal',
  admin:     'ember',
}

interface UserMenuProps {
  variant?: 'app' | 'public'
}

export default function UserMenu({ variant = 'app' }: UserMenuProps) {
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const syncRoles = useRoleStore((state) => state.syncRoles)
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  const handleLogout = async () => {
    setOpen(false)
    try {
      await authService.logout()
    } catch {
      // Best-effort: clear local state even when the API call fails.
    }
    logout()
    syncRoles([])
    navigate('/', { replace: true })
  }

  const initial = user?.firstName?.[0]?.toUpperCase() ?? 'K'
  const primaryRole = user?.roles?.[0]

  const triggerClass =
    variant === 'public'
      ? 'w-9 h-9 rounded-full bg-ember text-white flex items-center justify-center text-sm font-bold hover:opacity-90 transition-opacity'
      : 'w-8 h-8 rounded-full bg-[var(--accent)] flex items-center justify-center text-[11px] font-bold text-[var(--text-on-accent)] hover:opacity-90 transition-opacity'

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className={triggerClass}
      >
        {initial}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-60 rounded-xl border border-[var(--border-subtle)] bg-[var(--surface)] shadow-[var(--shadow-panel)] overflow-hidden z-50"
        >
          <div className="px-3 py-3 border-b border-[var(--border-subtle)]">
            <div className="text-sm font-bold text-[var(--text-primary)] truncate">
              {user ? `${user.firstName} ${user.lastName}` : 'Kaat User'}
            </div>
            <div className="text-xs text-[var(--text-muted)] truncate mb-2">
              {user?.email ?? ''}
            </div>
            {primaryRole && (
              <div className="flex flex-wrap gap-1">
                {user?.roles.map((r) => (
                  <Badge key={r} variant={ROLE_BADGE_VARIANT[r]} size="sm">
                    {r}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="py-1">
            <Link
              to="/profile"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--surface-muted)] transition-colors"
            >
              <UserIcon className="w-4 h-4 text-[var(--text-muted)]" />
              Profile
            </Link>
            <button
              role="menuitem"
              onClick={handleLogout}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--surface-muted)] transition-colors"
            >
              <LogOut className="w-4 h-4 text-[var(--text-muted)]" />
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
